import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Heart, Shield } from 'lucide-react';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1651915072949-95ebd33112cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxtYXVyaXRpdXMlMjBvY2VhbnxlbnwwfHx8fDE3NzYxNzQ1Nzd8MA&ixlib=rb-4.1.0&q=85"
            alt="Mauritius Ocean View"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-950/80 via-blue-900/70 to-blue-800/60" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
              Jouw Droomreis,
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-white">
                Perfect op Maat
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
              Wij helpen je bij het samenstellen van jouw perfecte reis, volledig afgestemd op jouw wensen, budget en reisstijl.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <Link
                to="/rondreizen-tool"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-900 font-semibold rounded-lg hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
              >
                Start je Reis
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/maatwerkreizen"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-md text-white font-semibold rounded-lg border-2 border-white/30 hover:bg-white/20 transition-all duration-300 transform hover:scale-105"
              >
                Bekijk Maatwerkreizen
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/70 rounded-full" />
          </div>
        </div>
      </section>

      {/* Waarom Kiezen Voor Ons */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4">
              Waarom Kiezen Voor Ons?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Wij geloven dat geen enkele reis hetzelfde moet zijn
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Heart,
                titel: "Persoonlijk Advies",
                beschrijving: "Elk reisplan wordt met zorg en aandacht speciaal voor jou samengesteld"
              },
              {
                icon: Sparkles,
                titel: "Reizen op Maat",
                beschrijving: "Volledig aangepast aan jouw wensen, voorkeuren en reisstijl"
              },
              {
                icon: ArrowRight,
                titel: "Flexibel & Snel",
                beschrijving: "Snel geregeld en altijd flexibel aan te passen waar nodig"
              },
              {
                icon: Shield,
                titel: "Elk Budget",
                beschrijving: "Van budget-vriendelijk tot luxe - voor ieder wat wils"
              }
            ].map((item) => (
              <div
                key={item.titel}
                className="group p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-white border border-blue-100 hover:border-blue-300 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-blue-900 to-blue-700 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-blue-900 mb-3">{item.titel}</h3>
                <p className="text-gray-600 leading-relaxed">{item.beschrijving}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hoe Werkt Het */}
      <section className="py-20 bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Hoe Werkt Het?
            </h2>
            <p className="text-xl text-blue-200 max-w-2xl mx-auto">
              In vier eenvoudige stappen naar jouw droomreis
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              {
                stap: "01",
                titel: "Deel je Wensen",
                beschrijving: "Vertel ons over je droomreis, budget en voorkeuren"
              },
              {
                stap: "02",
                titel: "Persoonlijk Voorstel",
                beschrijving: "Wij maken een op maat gemaakt reisvoorstel voor jou"
              },
              {
                stap: "03",
                titel: "Samen Afstemmen",
                beschrijving: "We verfijnen de reis tot alles perfect is"
              },
              {
                stap: "04",
                titel: "Zorgeloos Genieten",
                beschrijving: "Alles is geregeld, jij hoeft alleen nog te genieten!"
              }
            ].map((item, index) => (
              <div key={item.stap} className="relative">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300 transform hover:scale-105 h-full">
                  <div className="text-6xl font-bold text-white/20 mb-4">{item.stap}</div>
                  <h3 className="text-xl font-bold mb-3">{item.titel}</h3>
                  <p className="text-blue-200 leading-relaxed">{item.beschrijving}</p>
                </div>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-8 h-8 text-white/30" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-900 font-semibold rounded-lg hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
            >
              Start Vandaag
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-blue-900 to-blue-700 rounded-3xl p-12 text-center text-white relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full blur-3xl" />
              <div className="absolute bottom-0 right-0 w-60 h-60 bg-white rounded-full blur-3xl" />
            </div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Klaar Voor Jouw Avontuur?
              </h2>
              <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                Laat ons je helpen met het plannen van een onvergetelijke reis die perfect bij jou past
              </p>
              <Link
                to="/rondreizen-tool"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-900 font-semibold rounded-lg hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
              >
                Plan Je Reis Nu
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
